import { useQuery } from '@apollo/client';
import {
  DetailCard,
  EventQueryBuilderForm,
  reactSelectInlineError,
  getTranslatedText,
  AccessControl,
  TicketQueryBuilderForm,
  AppContext,
  COMMON_CONSTANTS
} from '@nile/components';
import { HeaderTag } from '@nile/components/src';
import {
  GET_CURRENT_USER,
  GET_ENTITY_PROPERTIES,
  GET_RULEBUILDER_OPERATORS,
  GET_TICKET_LISTVIEW_META
} from '@nile/graphql';
import ColumnConf from '@nile/mangifera/src/Containers/SheetContainers/ColumnConf';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { AccountClient } from '@nile/graphql';
import {
  Avatar,
  Checkbox,
  ResourceItem,
  ResourceList,
  TextStyle
} from '@superops/ui-design-system';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import {
  entityShareType,
  shareTypeList
} from '@nile/mangifera/src/Constants/Tickets';
import ShareViewSelector from '@nile/mangifera/src/Containers/SelectContainers/ShareViewSelector';

function ViewMainContainer(props) {
  const {
    intl,
    view,
    options,
    errors,
    defaultSelectedColumns,
    entityShare
  } = props;
  const [error, setErrors] = useState([]);
  const {
    loading: operatorsLoading,
    error: operatorsError,
    data: allOperators
  } = useQuery(GET_RULEBUILDER_OPERATORS);
  const {
    loading: entitiesLoading,
    error: entitiesError,
    data: allEntities
  } = useQuery(GET_ENTITY_PROPERTIES, { client: AccountClient });
  const {
    loading: ticketListViewMetaLoading,
    error: ticketAutomationError,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });
  const [personalization, setPersonalization] = useState({});
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};

  const [viewName, setViewName] = useState({
    name: props.view.name ? props.view.name : ''
  });

  const [shareViewWith, setShareViewWith] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const isTechGroup = shareViewWith === shareTypeList.SPECIFIC_GROUP;
  const shareType = entityShare?.userShareType || entityShare?.groupShareType;

  useEffect(() => {
    if (props.view.id && entityShare) {
      if (shareType === entityShareType.ALL) {
        setShareViewWith(shareTypeList.ALL);
      } else if (entityShare?.userShareType === entityShareType.SPECIFIC) {
        setShareViewWith(shareTypeList.SPECIFIC_USER);
        setTechnicians(
          entityShare?.users?.map((user) => ({
            value: user.userId,
            label: user.name ? `${user.name} (${user.email})` : user.email,
            email: user.email
          })) || []
        );
      } else if (entityShare?.groupShareType === entityShareType.SPECIFIC) {
        setShareViewWith(shareTypeList.SPECIFIC_GROUP);
        setTechnicians(
          entityShare?.groups?.map((group) => ({
            value: group.groupId,
            label: group.name
          })) || []
        );
      }
    }
  }, [props?.view?.id]);

  const updateName = (name) => {
    setViewName({ name: name });
    context.updateContext('name', name);
  };

  const context = useContext(TicketContext);
  const customColumns = props?.personalization?.value
    ? JSON.parse(props.personalization.value)
    : [];
  const [columns, setColumns] = useState(customColumns);
  const [defaultColumns, setDefaultColumns] = useState([]);
  const back = useCallback(() => {
    props.history.push('/ticket/1/all');
  }, []); // eslint-disable-line

  const sections = [];
  const allFields = [];

  const columnsSection = {
    name: 'user',
    header: getTranslatedText(intl, 'ticket.newView.configure.h2')
  };

  const changeSharedView = useCallback((selected) => {
    context.updateContext('shared', selected);
    if (!selected) {
      updateShareRadioBtn('');
      context.updateShareView({});
      return;
    }
    const newShareType =
      shareType === entityShareType.ALL
        ? shareTypeList.ALL
        : entityShare?.userShareType === entityShareType.SPECIFIC
        ? shareTypeList.SPECIFIC_USER
        : entityShare?.groupShareType === entityShareType.SPECIFIC
        ? shareTypeList.SPECIFIC_GROUP
        : shareTypeList.ALL;
    updateShareRadioBtn(newShareType);
  });

  const updateShareRadioBtn = (value) => {
    // update the shareViewWith state
    setShareViewWith(value);
    // reset the technicians state
    setTechnicians([]);
    if (value !== shareTypeList.ALL) {
      addEntityShare(value, []);
    } else {
      let shareView = {
        userShareType: entityShareType.ALL
      };
      context.updateShareView(shareView);
    }
  };

  const addEntityShare = (shareWith, technicians) => {
    let shareView = {};
    if (shareWith === shareTypeList.SPECIFIC_USER) {
      // if specific technician is selected then update the entityShare in context to pass the value to API
      shareView = {
        userShareType: entityShareType.SPECIFIC,
        userIds: technicians?.reduce((result, tech) => {
          result.push({
            userId: tech.value,
            email: tech.email
          });
          return result;
        }, [])
      };
    } else if (shareWith === shareTypeList.SPECIFIC_GROUP) {
      // if specific technician group is selected then update the entityShare in context to pass the value to API
      shareView = {
        groupShareType: entityShareType.SPECIFIC,
        groupIds: technicians?.reduce((result, tech) => {
          result.push({ groupId: tech.value });
          return result;
        }, [])
      };
    }
    context.updateShareView(shareView);
  };

  const updateTechniciansDropdown = (field, option) => {
    const selectedOption = option || [];
    selectedOption?.map((option) => {
      return {
        ...option,
        value: isTechGroup ? option.groupId : option.userId,
        label: option.name
      };
    }) || [];
    setTechnicians(selectedOption);
    if (shareViewWith !== shareTypeList.ALL) {
      addEntityShare(shareViewWith, selectedOption);
    }
  };

  useEffect(() => {
    setErrors(props.errors);
  }, [props.errors]);

  if (ticketListViewMetaLoading) return <div>loading...</div>;
  if (!entitiesLoading) {
    const Ticket = allEntities.getEntitiesMeta
      .find((item) => {
        return item.name === 'Ticket';
      })
      .attributes.filter((el) => {
        return (
          ticketListViewMeta?.getTicketListViewMetaInfo?.selectAttributes.indexOf(
            el.name
          ) > -1
        );
      });
    columnsSection.items = [];
    const keys = Object.keys(Ticket);
    for (let k = 0; k < keys.length; k++) {
      columnsSection.items.push({
        ...Ticket[keys[k]],
        id: Ticket[keys[k]].name,
        label: getTranslatedText(intl, Ticket[keys[k]].label)
      });
    }
  }

  const updateParent = (selectedColumns) => {
    const colsToSave = selectedColumns || defaultColumns;
    setColumns(colsToSave);
    const personalization = {
      user: {
        firstName: currentUser.firstName,
        userId: currentUser.userId,
        email: currentUser.email,
        lastName: currentUser.lastName
      },
      category: 'TicketView',
      value: colsToSave && colsToSave.length ? JSON.stringify(colsToSave) : ''
    };
    context.updateProperty('personalization', personalization);
  };

  sections.push(columnsSection);

  const actions = {
    updateName: updateName,
    back: back
  };

  const children = (
    <AccessControl
      roleToCheck="ticketSharedViews.allow"
      render={
        <div className="fx col mt-20">
          <Checkbox
            label={getTranslatedText(intl, 'ticket.newView.checkBox.share')}
            checked={props.view.shared}
            onChange={changeSharedView}
          />
          {props.view.shared && (
            <ShareViewSelector
              intl={intl}
              isTechGroup={isTechGroup}
              shareViewWith={shareViewWith}
              onShareTypeChange={updateShareRadioBtn}
              onTechniciansUpdate={updateTechniciansDropdown}
              technicians={technicians}
              errors={props.errors}
            />
          )}
        </div>
      }
    />
  );

  return (
    <div>
      <DetailCard
        children={children}
        meta={{ title: getTranslatedText(intl, 'ticket.newView.h2.details') }}
        actions={actions}
        disableActive
        noDesciption
        notSectioned
        data={viewName}
        errors={errors?.name}
        noCard
      />
      <div className="mt40 mb40">
        {!ticketListViewMetaLoading &&
          !entitiesLoading &&
          !operatorsLoading && (
            <TicketQueryBuilderForm
              meta={ticketListViewMeta.getTicketListViewMetaInfo}
              allEntities={allEntities}
              allOperators={allOperators}
              context={context}
              query={context.viewCondition.ruleCondition}
              errors={errors?.['viewCondition.operands']}
            />
          )}
        {typeof errors?.['viewCondition.operands']?.[0] === 'string' &&
          reactSelectInlineError(errors?.['viewCondition.operands'])}
      </div>
      <div className="flex center">
        <HeaderTag
          isBgStrip
          title={getTranslatedText(intl, 'ticket.newView.chooseCol')}
          isNewLayout
        />
        {!entitiesLoading && (
          <div className="mlauto">
            <ColumnConf
              columns={columns.length || defaultSelectedColumns.length}
              sections={sections}
              actions={actions}
              meta={{
                heading: getTranslatedText(
                  intl,
                  'ticket.newView.configure.header'
                ),
                selectedTitle: getTranslatedText(
                  intl,
                  'ticket.newView.configure.select'
                )
              }}
              data={allFields}
              header={<div>head</div>}
              selectedItems={columns.length ? columns : defaultSelectedColumns}
              updateParent={updateParent}
            />
          </div>
        )}
      </div>
      {errors?.['personalization.value']
        ? reactSelectInlineError(errors?.['personalization.value'][0])
        : ''}
      <div className="fx wrap scroll">
        <ResourceList
          resourceName={{
            singular: 'Ticket Column',
            plural: 'Ticket Columns'
          }}
          items={columns.length ? columns : defaultSelectedColumns}
          renderItem={(item) => {
            const { id, url, name, location } = item;
            const media = <Avatar customer size="medium" name={name} />;

            return (
              <ResourceItem
                id={id}
                accessibilityLabel={`View details for ${item.label}`}
                customClassName="mt-10 ml-12"
              >
                <h3>
                  <TextStyle variation="strong">{item.label}</TextStyle>
                </h3>
              </ResourceItem>
            );
          }}
        />
      </div>
    </div>
  );
}

function ViewContainer(props) {
  const {
    intl,
    view,
    personalization,
    errors,
    defaultSelectedColumns,
    entityShare
  } = props;
  return (
    <React.Fragment>
      <ViewMainContainer
        intl={intl}
        view={view}
        personalization={personalization}
        defaultSelectedColumns={defaultSelectedColumns}
        errors={errors}
        entityShare={entityShare}
      ></ViewMainContainer>
    </React.Fragment>
  );
}

export default injectIntl(ViewContainer);
